// Precios y cupos vigentes, leídos de la base (función pública `public-pricing`). Es la misma
// fuente que usa el checkout: el sitio no puede mostrar un precio distinto del que se cobra.
// Si la consulta falla, devuelve null y `commercialPricing.js` usa sus valores de respaldo.
import { useEffect, useState } from "react";

let cache = null;
let inflight = null;
// `true` mientras `cache` viene del prerender (window.__INITIAL_OFFER__) y todavía no se revalidó
// contra la base: el primer render usa esa oferta para coincidir con el HTML, y después se refresca.
let primed = false;

const isOffer = (body) => body?.version === 1 && Boolean(body?.plans);

// Estado inicial desde el prerender. Se llama ANTES de renderizar/hidratar (index.jsx, entry-server):
// así el primer render del cliente usa exactamente los precios que quedaron en el HTML.
export function primeCommercialOffer(offer) {
  if (!isOffer(offer)) return;
  cache = offer;
  primed = true;
}

export async function fetchCommercialOffer(fetchImpl = fetch, { revalidate = false } = {}) {
  if (cache && !revalidate) return cache;
  if (!inflight) {
    const base = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    inflight = (async () => {
      try {
        const res = await fetchImpl(`${base}/functions/v1/public-pricing`, { headers: key ? { apikey: key } : {} });
        if (!res.ok) return null;
        const body = await res.json();
        if (!isOffer(body)) return null;
        cache = body;
        primed = false;
        return body;
      } catch {
        return null;
      } finally {
        inflight = null;
      }
    })();
  }
  return inflight;
}

export function resetCommercialOfferCache() {
  cache = null;
  inflight = null;
  primed = false;
}

export default function useCommercialOffer() {
  const [offer, setOffer] = useState(cache);
  useEffect(() => {
    let alive = true;
    // Sin oferta: la pedimos. Con oferta del prerender: la revalidamos (un precio cambiado después del
    // build se corrige acá, ya hidratado, sin romper la hidratación).
    if (!cache || primed) fetchCommercialOffer(fetch, { revalidate: primed }).then((o) => { if (alive && o) setOffer(o); });
    return () => { alive = false; };
  }, []);
  return offer;
}
